/**
 * People/Workforce Phase 1B — pure obligation projection helpers.
 *
 * Source contract: docs/source-of-truth/people-workforce-phase1b-contract.md
 * SQL owner: supabase/migrations/20270402000000_people_workforce_phase1b_projection_overview.sql
 *
 * Wave 1B is additive: no generic obligation table, no transactional gate
 * authority, no global compliant/ready badge.
 */

import { formatEnum } from '@/lib/format';
import { WORKFORCE_CAPABILITY_ROLE_GRANTS } from './phase1a-capabilities';

export const WORKER_OBLIGATION_STATUSES = [
  'NOT_APPLICABLE',
  'PENDING',
  'AWAITING_EVIDENCE',
  'AWAITING_VERIFICATION',
  'CONDITIONAL',
  'CURRENT',
  'DUE_SOON',
  'OVERDUE',
  'EXPIRED',
  'REJECTED',
  'WAIVED',
  'SUSPENDED',
  'DEGRADED',
] as const;

export type WorkerObligationStatus = (typeof WORKER_OBLIGATION_STATUSES)[number];

export const WORKER_OBLIGATION_URGENCY_GROUPS = [
  'cannot_work',
  'evidence_awaiting',
  'due_30',
  'due_60',
  'due_90',
  'new_starters',
  'development',
  'lifecycle',
  'unowned_degraded',
  'waived',
  'current',
] as const;

export type WorkerObligationUrgencyGroup = (typeof WORKER_OBLIGATION_URGENCY_GROUPS)[number];

export const WORKER_OBLIGATION_NEXT_ACTION_TYPES = [
  'REVIEW_EVIDENCE',
  'COLLECT_EVIDENCE',
  'VERIFY',
  'ACKNOWLEDGE_POLICY',
  'COMPLETE_TRAINING',
  'COMPLETE_ONBOARDING',
  'LINK_LOGIN',
  'CLASSIFY_ENGAGEMENT',
  'SCHEDULE_PROBATION',
  'VIEW_WORKER',
  'RESOLVE_OWNERSHIP',
  'RENEW',
] as const;

export type WorkerObligationNextActionType = (typeof WORKER_OBLIGATION_NEXT_ACTION_TYPES)[number];

export const WORKFORCE_PEOPLE_VIEW_ROLES =
  WORKFORCE_CAPABILITY_ROLE_GRANTS['workforce.people.view'];

/** Canonical domain sources adapted in Phase 1B. */
export const PHASE1B_CANONICAL_SOURCE_TABLES = [
  'worker_engagements',
  'worker_readiness_projection_controlled',
  'workforce_screening_assessments',
  'worker_screenings',
  'worker_onboarding_documents',
  'worker_certifications',
  'training_assignments',
  'workforce_mandatory_training_cycles',
  'staff_policy_requirements',
  'policy_acknowledgements',
  'worker_probation_milestones',
  'clinical_competency_attestations',
  'clinical_supervision_events',
] as const;

/** Explicitly forbidden as obligation adapters in Wave 1B. */
export const PHASE1B_FORBIDDEN_SOURCE_TABLES = [
  'staff_qualifications',
  'probation_tracking',
  'training_records',
  'clinical_supervision_logs',
] as const;

const DEEP_LINK_PREFIXES = [
  '/me/work-readiness',
  '/me/onboarding',
  '/me/training',
  '/me/policies',
  '/me/renewals',
  '/me/performance',
  '/me/supervision',
  '/me/credentials',
  '/me/compliance',
  '/people/workers',
  '/people/onboarding',
  '/people/offboarding',
  '/people/learning',
  '/people/policies-conduct',
  '/people/development/performance',
  '/people/development/supervision',
  '/people/compliance/screening',
  '/people/compliance/certifications',
] as const;

export type ObligationImpacts = {
  first_shift: boolean;
  assignment: boolean;
  bau: boolean;
  development: boolean;
};

export type ObligationImpactLabel =
  | 'Cannot be assigned'
  | 'Not ready for first shift'
  | 'Policy warning'
  | 'BAU follow-up'
  | 'Development due'
  | 'Unable to determine';

export function obligationStatusBlocksWork(status: WorkerObligationStatus): boolean {
  return [
    'PENDING',
    'AWAITING_EVIDENCE',
    'AWAITING_VERIFICATION',
    'OVERDUE',
    'EXPIRED',
    'REJECTED',
    'SUSPENDED',
    'DEGRADED',
  ].includes(status);
}

export function parseIsoDateOnly(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return null;
  return trimmed;
}

export function daysBetween(asOf: string, target: string): number {
  const a = Date.parse(`${asOf}T00:00:00Z`);
  const b = Date.parse(`${target}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) {
    throw new Error('invalid_date');
  }
  return Math.round((b - a) / 86_400_000);
}

/**
 * Pure status mapper mirroring SQL worker_obligation_status_from_dates.
 * Unknown source status → DEGRADED (never silent CURRENT).
 */
export function obligationStatusFromDates(input: {
  sourceStatus: string | null | undefined;
  dueDate?: string | null;
  expiryDate?: string | null;
  asOf: string;
  dueSoonDays?: number;
}): WorkerObligationStatus {
  const asOf = parseIsoDateOnly(input.asOf);
  if (!asOf) return 'DEGRADED';
  const window = Math.max(input.dueSoonDays ?? 30, 1);
  const source = (input.sourceStatus ?? 'PENDING').trim().toUpperCase();
  const due = parseIsoDateOnly(input.dueDate ?? null);
  const expiry = parseIsoDateOnly(input.expiryDate ?? null);

  if (source === 'REJECTED') return 'REJECTED';
  if (source === 'REVOKED') return 'SUSPENDED';
  if (
    source === 'SUSPENDED' ||
    source === 'WAIVED' ||
    source === 'NOT_APPLICABLE' ||
    source === 'DEGRADED' ||
    source === 'CONDITIONAL'
  ) {
    return source;
  }
  if (source === 'EXPIRED') return 'EXPIRED';
  if (source === 'OVERDUE') return 'OVERDUE';

  if (
    source === 'AWAITING_EVIDENCE' ||
    source === 'AWAITING_VERIFICATION' ||
    source === 'PENDING'
  ) {
    if (due && daysBetween(asOf, due) < 0) return 'OVERDUE';
    return source as WorkerObligationStatus;
  }

  if (expiry && daysBetween(asOf, expiry) < 0) return 'EXPIRED';
  if (due && daysBetween(asOf, due) < 0) return 'OVERDUE';
  if (expiry && daysBetween(asOf, expiry) >= 0 && daysBetween(asOf, expiry) <= window) {
    return 'DUE_SOON';
  }
  if (due && daysBetween(asOf, due) >= 0 && daysBetween(asOf, due) <= window) {
    return 'DUE_SOON';
  }

  if (
    source === 'CURRENT' ||
    source === 'CLEARED' ||
    source === 'COMPLETED' ||
    source === 'ACKNOWLEDGED' ||
    source === 'VERIFIED'
  ) {
    return 'CURRENT';
  }

  return 'DEGRADED';
}

/** Conservative credential status mirror: verification is required for currency. */
export function certificationObligationStatus(input: {
  sourceStatus: string | null | undefined;
  verificationStatus: string | null | undefined;
  expiryDate?: string | null;
  asOf: string;
}): WorkerObligationStatus {
  const source = (input.sourceStatus ?? 'PENDING').trim().toUpperCase();
  const verification = (input.verificationStatus ?? 'UNVERIFIED').trim().toUpperCase();

  if (
    source === 'SUSPENDED' ||
    source === 'REVOKED' ||
    source === 'REJECTED' ||
    source === 'EXPIRED'
  ) {
    return obligationStatusFromDates({
      sourceStatus: source,
      expiryDate: input.expiryDate,
      asOf: input.asOf,
    });
  }
  if (verification === 'REJECTED') return 'REJECTED';
  if (verification !== 'VERIFIED') return 'AWAITING_VERIFICATION';
  return obligationStatusFromDates({
    sourceStatus: source,
    expiryDate: input.expiryDate,
    asOf: input.asOf,
  });
}

/**
 * Half-open effective window: effective_from <= asOf AND (effective_to IS NULL OR effective_to > asOf).
 */
export function isHalfOpenEffective(input: {
  effectiveFrom: string;
  effectiveTo?: string | null;
  asOf: string;
}): boolean {
  const asOf = parseIsoDateOnly(input.asOf);
  const from = parseIsoDateOnly(input.effectiveFrom);
  if (!asOf || !from) return false;
  if (daysBetween(asOf, from) > 0) return false; // from after asOf
  if (!input.effectiveTo) return true;
  const to = parseIsoDateOnly(input.effectiveTo);
  if (!to) return false;
  return daysBetween(asOf, to) > 0; // asOf < effective_to
}

export function impactLabels(
  impacts: ObligationImpacts,
  status?: WorkerObligationStatus,
  requirementType?: string | null,
): ObligationImpactLabel[] {
  const labels: ObligationImpactLabel[] = [];
  const blocksWork = status ? obligationStatusBlocksWork(status) : true;
  const isNoAction = status === 'CURRENT' || status === 'WAIVED' || status === 'NOT_APPLICABLE';
  if (requirementType?.toUpperCase() === 'LOGIN') return isNoAction ? [] : ['Policy warning'];
  if (impacts.assignment && blocksWork) labels.push('Cannot be assigned');
  if (impacts.first_shift && blocksWork) labels.push('Not ready for first shift');
  if (
    impacts.bau &&
    !isNoAction &&
    (!blocksWork || (!impacts.assignment && !impacts.first_shift))
  ) {
    labels.push('BAU follow-up');
  }
  if (impacts.development && !isNoAction) labels.push('Development due');
  if (status === 'DEGRADED') labels.push('Unable to determine');
  return labels;
}

export function urgencyGroupFor(input: {
  status: WorkerObligationStatus;
  requirementType?: string | null;
  impacts: ObligationImpacts;
  dueDate?: string | null;
  expiryDate?: string | null;
  asOf: string;
  recipientState?: string | null;
  isNewStarter?: boolean;
}): WorkerObligationUrgencyGroup {
  const status = input.status;
  const reqType = (input.requirementType ?? '').toUpperCase();
  const recipient = (input.recipientState ?? '').toUpperCase();
  if (status === 'DEGRADED') {
    return 'unowned_degraded';
  }
  if (status === 'CURRENT') return 'current';
  if (status === 'WAIVED' || status === 'NOT_APPLICABLE') return 'waived';

  if (
    reqType !== 'LOGIN' &&
    obligationStatusBlocksWork(status) &&
    (input.impacts.first_shift || input.impacts.assignment)
  ) {
    return 'cannot_work';
  }

  if (recipient === 'UNOWNED' || recipient === 'OUT_OF_SCOPE') return 'unowned_degraded';

  if (
    status === 'AWAITING_EVIDENCE' ||
    status === 'AWAITING_VERIFICATION' ||
    status === 'CONDITIONAL'
  ) {
    return 'evidence_awaiting';
  }

  if (
    input.isNewStarter ||
    reqType === 'ONBOARDING' ||
    reqType === 'LOGIN' ||
    reqType === 'FIRST_SHIFT'
  ) {
    return 'new_starters';
  }
  if (
    input.impacts.development ||
    reqType === 'PROBATION' ||
    reqType === 'SUPERVISION' ||
    reqType === 'PERFORMANCE'
  ) {
    return 'development';
  }
  if (reqType === 'ENGAGEMENT') return 'lifecycle';

  const anchor =
    parseIsoDateOnly(input.expiryDate ?? null) ?? parseIsoDateOnly(input.dueDate ?? null);
  const asOf = parseIsoDateOnly(input.asOf);
  if (anchor && asOf) {
    const days = daysBetween(asOf, anchor);
    if (days <= 30) return 'due_30';
    if (days <= 60) return 'due_60';
    if (days <= 90) return 'due_90';
  }

  if (status === 'DUE_SOON' || status === 'OVERDUE' || status === 'PENDING') {
    return 'due_30';
  }

  return 'unowned_degraded';
}

export function isAllowlistedDeepLink(href: string | null | undefined): boolean {
  if (!href) return false;
  const value = href.trim();
  if (!value.startsWith('/')) return false;
  if (/^(https?:|\/\/|javascript:|data:)/i.test(value)) return false;
  if (!/^\/[a-z0-9][a-z0-9/_?=&%-]*$/i.test(value)) return false;
  if (value === '/people') return true;
  return DEEP_LINK_PREFIXES.some(
    (prefix) =>
      value === prefix || value.startsWith(`${prefix}/`) || value.startsWith(`${prefix}?`),
  );
}

export function nextActionFor(input: {
  status: WorkerObligationStatus;
  requirementType: string;
  staffId: string;
}): { type: WorkerObligationNextActionType; href: string } {
  const type = input.requirementType.toUpperCase();
  const staffId = input.staffId;

  if (type === 'ENGAGEMENT') {
    return { type: 'CLASSIFY_ENGAGEMENT', href: `/people/workers/${staffId}?tab=employment` };
  }
  if (type === 'LOGIN') {
    return { type: 'LINK_LOGIN', href: `/people/workers/${staffId}?tab=employment` };
  }
  if (type === 'ONBOARDING' || type === 'FIRST_SHIFT') {
    return {
      type: 'COMPLETE_ONBOARDING',
      href: `/people/workers/${staffId}?tab=onboarding`,
    };
  }
  if (type === 'SCREENING') {
    if (input.status === 'AWAITING_VERIFICATION') {
      return { type: 'VERIFY', href: `/people/compliance/screening?staffId=${staffId}` };
    }
    if (input.status === 'DUE_SOON' || input.status === 'EXPIRED' || input.status === 'OVERDUE') {
      return { type: 'RENEW', href: `/people/compliance/screening?staffId=${staffId}` };
    }
    return { type: 'COLLECT_EVIDENCE', href: `/people/compliance/screening?staffId=${staffId}` };
  }
  if (type === 'CERTIFICATION') {
    const href = `/people/compliance/certifications/${staffId}`;
    if (input.status === 'AWAITING_VERIFICATION') {
      return { type: 'VERIFY', href };
    }
    if (input.status === 'DUE_SOON' || input.status === 'EXPIRED' || input.status === 'OVERDUE') {
      return { type: 'RENEW', href };
    }
    return {
      type: 'REVIEW_EVIDENCE',
      href,
    };
  }
  if (type === 'WORK_RIGHTS') {
    if (input.status === 'DUE_SOON' || input.status === 'EXPIRED' || input.status === 'REJECTED') {
      return { type: 'RENEW', href: `/people/onboarding/documents?staffId=${staffId}` };
    }
    if (input.status === 'AWAITING_VERIFICATION') {
      return { type: 'VERIFY', href: `/people/onboarding/documents?staffId=${staffId}` };
    }
    return {
      type: 'REVIEW_EVIDENCE',
      href: `/people/onboarding/documents?staffId=${staffId}`,
    };
  }
  if (type === 'TRAINING') {
    return { type: 'COMPLETE_TRAINING', href: `/people/learning?staffId=${staffId}` };
  }
  if (type === 'POLICY_ACK' || type === 'CODE') {
    return {
      type: 'ACKNOWLEDGE_POLICY',
      href: `/people/workers/${staffId}?tab=policies`,
    };
  }
  if (type === 'PROBATION' || type === 'PERFORMANCE') {
    return {
      type: 'SCHEDULE_PROBATION',
      href: `/people/workers/${staffId}?tab=development`,
    };
  }
  if (type === 'SUPERVISION' || type === 'COMPETENCY') {
    return { type: 'REVIEW_EVIDENCE', href: `/people/workers/${staffId}?tab=development` };
  }
  if (input.status === 'DEGRADED') {
    return { type: 'RESOLVE_OWNERSHIP', href: `/people/workers/${staffId}` };
  }
  return { type: 'VIEW_WORKER', href: `/people/workers/${staffId}` };
}

/** Coordinator English for next-action types. Never render the enum token. */
export const WORKER_OBLIGATION_NEXT_ACTION_LABELS: Record<WorkerObligationNextActionType, string> =
  {
    REVIEW_EVIDENCE: 'Review evidence',
    COLLECT_EVIDENCE: 'Collect evidence',
    VERIFY: 'Verify',
    ACKNOWLEDGE_POLICY: 'Open policies',
    COMPLETE_TRAINING: 'Open learning',
    COMPLETE_ONBOARDING: 'Open onboarding',
    LINK_LOGIN: 'Link login',
    CLASSIFY_ENGAGEMENT: 'Review employment',
    SCHEDULE_PROBATION: 'Open development',
    VIEW_WORKER: 'Open worker',
    RESOLVE_OWNERSHIP: 'Open worker',
    RENEW: 'Renew',
  };

function isUnscopedPeopleQueue(href: string): boolean {
  const path = href.split('?')[0] ?? href;
  return (
    path === '/people/onboarding' ||
    path === '/people/onboarding/documents' ||
    path === '/people/policies-conduct/assignments' ||
    path === '/people/development/supervision' ||
    path === '/people/development/performance' ||
    path === '/people/compliance/certifications' ||
    path === '/people/learning' ||
    path === '/people/learning/competencies' ||
    path === '/people/offboarding' ||
    path === '/people/compliance/screening'
  );
}

/**
 * SQL projections still emit unscoped queue hrefs for some types. Rewrite at
 * the operator surface so the click lands on the person (or their journey),
 * never a tenant-wide list.
 */
export function personScopedObligationAction(item: WorkerObligationItem): {
  type: WorkerObligationNextActionType;
  href: string;
  label: string;
} {
  const staffId = item.staff_id;
  const rawType = String(item.next_action?.type ?? 'VIEW_WORKER').toUpperCase();
  const type = (
    WORKER_OBLIGATION_NEXT_ACTION_TYPES.includes(rawType as WorkerObligationNextActionType)
      ? rawType
      : 'VIEW_WORKER'
  ) as WorkerObligationNextActionType;
  const fallback = nextActionFor({
    status: item.status,
    requirementType: item.requirement_type,
    staffId,
  });
  let href = (item.next_action?.href ?? fallback.href).trim();

  if (type === 'CLASSIFY_ENGAGEMENT' && fallback.type === 'CLASSIFY_ENGAGEMENT') {
    // A projection may point at the worker overview, which cannot classify employment.
    href = fallback.href;
  } else if (
    type === 'COMPLETE_ONBOARDING' &&
    item.source_assignment_table === 'worker_onboarding_journeys' &&
    item.source_assignment_id
  ) {
    href = `/people/onboarding/${encodeURIComponent(item.source_assignment_id)}`;
  } else if (!href || isUnscopedPeopleQueue(href)) {
    href = fallback.href;
  } else if ((href.split('?')[0] ?? href) === '/people/compliance/certifications') {
    href = `/people/compliance/certifications/${staffId}`;
  }

  if (!isAllowlistedDeepLink(href)) {
    href = `/people/workers/${staffId}`;
  }

  return {
    type,
    href,
    label: WORKER_OBLIGATION_NEXT_ACTION_LABELS[type] ?? 'Open worker',
  };
}

export function engagementObligationStatus(input: {
  classificationState?: string | null;
  workerClassification?: string | null;
  accountabilityResolution?: string | null;
  hasRow: boolean;
}): WorkerObligationStatus {
  if (!input.hasRow) return 'DEGRADED';
  if (input.classificationState !== 'CONFIRMED') return 'DEGRADED';
  if (input.accountabilityResolution === 'UNABLE_TO_DETERMINE') return 'DEGRADED';
  if (
    input.workerClassification === 'UNCLASSIFIED' ||
    input.workerClassification === 'LEGACY_UNCLASSIFIED'
  ) {
    return 'DEGRADED';
  }
  return 'CURRENT';
}

export type WorkerObligationItem = {
  tenant_id: string;
  staff_id: string;
  staff_display_name?: string | null;
  obligation_key: string;
  requirement_code: string;
  requirement_type: string;
  authority_basis?: string | null;
  reason_codes?: string[] | null;
  status: WorkerObligationStatus;
  due_date?: string | null;
  expiry_date?: string | null;
  impacts: ObligationImpacts;
  impact_labels?: string[];
  accountable_capability?: string | null;
  recipient?: {
    state?: string | null;
    primary?: { role?: string | null; kind?: string | null } | null;
    escalation?: { kind?: string | null } | null;
    reason_code?: string | null;
  } | null;
  next_action?: { type?: string | null; href?: string | null } | null;
  urgency_group?: WorkerObligationUrgencyGroup | string | null;
  source_assignment_table?: string | null;
  source_assignment_id?: string | null;
  source_evidence_table?: string | null;
  source_evidence_id?: string | null;
  latest_source_event?: Record<string, unknown> | null;
  evidence_state?: string | null;
  delivery_state?: string | null;
  source_freshness_at?: string | null;
  projection_freshness_at?: string | null;
};

export type ListWorkerObligationsResponse = {
  items: WorkerObligationItem[];
  page: number;
  page_size: number;
  total_items: number;
  obligation_count: number;
  worker_count: number;
  urgency_counts: Partial<Record<WorkerObligationUrgencyGroup, number>>;
  filters?: Record<string, unknown>;
  as_of?: string;
  error?: string | null;
};

const SCREAMING_SNAKE_TOKEN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;
const PROGRAM_SUFFIX = /_(RAC|SAH)$/;
const W_GATE_CODE = /^W_?\d+/i;

function firstHumanEventTitle(event: Record<string, unknown> | null | undefined): string | null {
  if (!event) return null;
  for (const key of ['course_title', 'policy_title', 'title'] as const) {
    const value = event[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  const plain = event.plain_title;
  if (typeof plain !== 'string' || !plain.trim()) return null;
  const trimmed = plain.trim();
  if (W_GATE_CODE.test(trimmed)) return null;
  if (SCREAMING_SNAKE_TOKEN.test(trimmed)) {
    return formatEnum(trimmed.replace(PROGRAM_SUFFIX, ''));
  }
  return trimmed;
}

/**
 * Coordinator heading for an obligation. Never use `formatEnum(requirement_code)`
 * alone: W_05_ASSIGNABLE becomes "W 05 Assignable". Prefer course/policy titles
 * on the source event; W-gate codes fall back to requirement type.
 */
export function obligationRequirementTitle(
  item: Pick<WorkerObligationItem, 'requirement_code' | 'requirement_type'> & {
    latest_source_event?: Record<string, unknown> | null;
    plain_title?: string | null;
  },
): string {
  const fromEvent = firstHumanEventTitle(item.latest_source_event);
  if (fromEvent) return fromEvent;
  const plain = typeof item.plain_title === 'string' ? item.plain_title.trim() : '';
  if (plain && !W_GATE_CODE.test(plain) && !SCREAMING_SNAKE_TOKEN.test(plain)) {
    return plain;
  }
  const code = String(item.requirement_code ?? '').trim();
  if (code && !W_GATE_CODE.test(code)) {
    if (SCREAMING_SNAKE_TOKEN.test(code)) return formatEnum(code.replace(PROGRAM_SUFFIX, ''));
    return code;
  }
  const type = String(item.requirement_type ?? '').trim();
  if (type) return formatEnum(type);
  if (code) return formatEnum(code.replace(PROGRAM_SUFFIX, ''));
  return 'Work item';
}

export const URGENCY_GROUP_LABELS: Record<WorkerObligationUrgencyGroup, string> = {
  cannot_work: 'Cannot work / must stop',
  evidence_awaiting: 'Evidence awaiting review',
  due_30: 'Due within 30 days',
  due_60: 'Due within 60 days',
  due_90: 'Due within 90 days',
  new_starters: 'Not ready for first shift',
  development: 'Development due',
  lifecycle: 'Lifecycle changes',
  unowned_degraded: 'Unowned or degraded',
  waived: 'Waived / not applicable',
  current: 'Current / no action',
};

export function groupObligationsByUrgency(
  items: WorkerObligationItem[],
): Record<WorkerObligationUrgencyGroup, WorkerObligationItem[]> {
  const groups = Object.fromEntries(
    WORKER_OBLIGATION_URGENCY_GROUPS.map((g) => [g, [] as WorkerObligationItem[]]),
  ) as Record<WorkerObligationUrgencyGroup, WorkerObligationItem[]>;

  for (const item of items) {
    const key = (WORKER_OBLIGATION_URGENCY_GROUPS as readonly string[]).includes(
      String(item.urgency_group),
    )
      ? (item.urgency_group as WorkerObligationUrgencyGroup)
      : 'unowned_degraded';
    groups[key].push(item);
  }
  return groups;
}

export function countWorkers(items: WorkerObligationItem[]): number {
  return new Set(items.map((i) => i.staff_id).filter(Boolean)).size;
}

export function paginateDeterministically<T extends { obligation_key: string; staff_id: string }>(
  items: T[],
  page: number,
  pageSize: number,
): { items: T[]; page: number; page_size: number; total_items: number } {
  const safePage = Math.max(1, Math.floor(page) || 1);
  const safeSize = Math.min(100, Math.max(1, Math.floor(pageSize) || 25));
  const sorted = [...items].sort((a, b) => {
    const keyA = `${a.staff_id}:${a.obligation_key}`;
    const keyB = `${b.staff_id}:${b.obligation_key}`;
    return keyA.localeCompare(keyB);
  });
  const start = (safePage - 1) * safeSize;
  return {
    items: sorted.slice(start, start + safeSize),
    page: safePage,
    page_size: safeSize,
    total_items: sorted.length,
  };
}

export function parseListFilters(searchParams: Record<string, string | string[] | undefined>): {
  page: number;
  page_size: number;
  facility_id?: string;
  region_id?: string;
  role?: string;
  engagement_type?: string;
  worker_state?: string;
  owner?: string;
  authority_basis?: string;
  requirement_type?: string;
  status?: string;
  impact?: string;
  urgency_group?: string;
  search?: string;
  include_current: boolean;
} {
  const first = (key: string) => {
    const v = searchParams[key];
    return Array.isArray(v) ? v[0] : v;
  };

  const page = Math.max(1, Number.parseInt(first('page') ?? '1', 10) || 1);
  const page_size = Math.min(
    100,
    Math.max(1, Number.parseInt(first('page_size') ?? '25', 10) || 25),
  );

  const status = first('status')?.trim().toUpperCase();
  if (status && !(WORKER_OBLIGATION_STATUSES as readonly string[]).includes(status)) {
    throw new Error('worker_obligation_status_filter_invalid');
  }

  const impact = first('impact')?.trim().toLowerCase();
  if (
    impact &&
    !['first_shift', 'assignment', 'bau', 'development', 'cannot_work'].includes(impact)
  ) {
    throw new Error('worker_obligation_impact_filter_invalid');
  }

  const urgency_group = first('urgency_group')?.trim().toLowerCase();
  if (
    urgency_group &&
    !(WORKER_OBLIGATION_URGENCY_GROUPS as readonly string[]).includes(urgency_group)
  ) {
    throw new Error('worker_obligation_urgency_filter_invalid');
  }

  return {
    page,
    page_size,
    facility_id: first('facility_id')?.trim() || undefined,
    region_id: first('region_id')?.trim() || undefined,
    role: first('role')?.trim() || undefined,
    engagement_type: first('engagement_type')?.trim() || undefined,
    worker_state: first('worker_state')?.trim() || undefined,
    owner: first('owner')?.trim() || undefined,
    authority_basis: first('authority_basis')?.trim() || undefined,
    requirement_type: first('requirement_type')?.trim() || undefined,
    status: status || undefined,
    impact: impact || undefined,
    urgency_group: urgency_group || undefined,
    search: first('search')?.trim() || undefined,
    include_current: first('include_current') === '1' || first('include_current') === 'true',
  };
}

/** Professional work: admin may only appear as escalation, never performer. */
export function assertProfessionalAdminEscalationOnly(recipient: {
  state?: string | null;
  primary?: { role?: string | null; kind?: string | null } | null;
  escalation?: { kind?: string | null } | null;
  reason_code?: string | null;
}): boolean {
  const primaryRole = (recipient.primary?.role ?? '').toUpperCase();
  const primaryKind = (recipient.primary?.kind ?? '').toUpperCase();
  if (primaryKind === 'QUALIFIED_PERFORMER') {
    return !['SYSTEM_ADMIN', 'TENANT_ADMIN'].includes(primaryRole);
  }
  if (recipient.state === 'UNOWNED' && recipient.reason_code === 'NO_QUALIFIED_PROFESSIONAL') {
    return recipient.escalation?.kind === 'ADMIN_ESCALATION' || recipient.escalation?.kind == null;
  }
  return true;
}
